import { z } from 'zod';

// src/addresses/model.ts
var ADDRESS_TYPES = [
  "wallet",
  "contract",
  "token",
  "nft",
  "exchange",
  "dex",
  "dex_pool",
  "proxy",
  "eoa",
  "unknown"
];
var ADDRESS_TYPE_LABEL = {
  wallet: "Wallet",
  contract: "Contract",
  token: "Token",
  nft: "NFT",
  exchange: "Exchange",
  dex: "DEX",
  dex_pool: "DEX pool",
  proxy: "Proxy",
  eoa: "EOA",
  unknown: "Unknown"
};
var CONTRACT_ADDRESS_TYPES = /* @__PURE__ */ new Set([
  "contract",
  "token",
  "nft",
  "proxy",
  "dex",
  "dex_pool"
]);
function isContractType(type) {
  return type != null && CONTRACT_ADDRESS_TYPES.has(type);
}
var ADDRESS_LIST_SCOPES = ["tenant", "entity", "cross_chain"];
var ADDRESS_ROW_SCOPES = ["user", "tenant", "org", "customer", "global"];
var ADDRESS_ROW_SCOPE_LABELS = {
  user: "Your view",
  tenant: "Shared with tenant",
  org: "Organization",
  customer: "Customer",
  global: "Global"
};
var ADDRESS_ROW_SCOPE_DESCRIPTIONS = {
  user: "Your own view (your private overrides on top of the shared tiers).",
  tenant: "The view shared across your tenant (drops your personal overrides).",
  org: "An organization-level view, shared across this org's tenants.",
  customer: "A customer-level view, shared across the whole account.",
  global: "The shared global record of this address (no tenant overrides)."
};
function addressRowScopeLabel(scope) {
  return ADDRESS_ROW_SCOPE_LABELS[scope] ?? scope;
}
function addressRowScopeDescription(scope) {
  return ADDRESS_ROW_SCOPE_DESCRIPTIONS[scope] ?? "";
}
var ADDRESS_DEFAULT_PAGE_LIMIT = 50;
var ADDRESS_MAX_PAGE_LIMIT = 200;
var ADDRESS_SCOPES = {
  read: "addresses:show",
  create: "addresses:create",
  edit: "addresses:edit",
  delete: "addresses:delete",
  share: "addresses:share",
  manageDefaults: "addresses:manage_defaults",
  mapEntity: "addresses:map_entity",
  enrich: "addresses:enrich"
};

// src/defi/position.ts
var PositionKind = {
  SUPPLY: "supply",
  BORROW: "borrow",
  STAKE: "stake",
  LIQUIDITY: "liquidity",
  FARM: "farm",
  VAULT: "vault",
  OTHER: "other"
};

// src/addresses/wallet-data.ts
var enc = encodeURIComponent;
var walletDataPaths = {
  defi: (chain, address) => `/addresses/${enc(chain)}/${enc(address)}/defi`,
  nfts: (chain, address) => `/addresses/${enc(chain)}/${enc(address)}/nfts`,
  nftCollection: (chain, address) => `/addresses/${enc(chain)}/${enc(address)}/nft-collection`,
  domain: (chain, address) => `/addresses/${enc(chain)}/${enc(address)}/domain`
};
function numOrUndef(v) {
  if (v == null) return void 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : void 0;
}
function defiKindFromLabel(label) {
  const l = (label ?? "").toLowerCase();
  if (l.includes("stak")) return PositionKind.STAKE;
  if (l.includes("borrow") || l.includes("debt") || l.includes("loan")) return PositionKind.BORROW;
  if (l.includes("supply") || l.includes("lend") || l.includes("deposit")) return PositionKind.SUPPLY;
  if (l.includes("liquidity") || l.includes(" lp") || l.endsWith("lp") || l.includes("pool")) return PositionKind.LIQUIDITY;
  if (l.includes("farm")) return PositionKind.FARM;
  if (l.includes("vault")) return PositionKind.VAULT;
  return PositionKind.OTHER;
}
function tokenToAsset(token, chainId) {
  return {
    symbol: token.symbol ?? token.name ?? "?",
    address: token.contract_address ?? void 0,
    chainId,
    amount: numOrUndef(token.balance_formatted),
    amountRaw: token.balance ?? void 0,
    decimals: token.decimals ?? void 0,
    priceUsd: token.usd_price ?? null,
    usdValue: token.usd_value ?? null,
    logoUrl: token.logo ?? token.thumbnail ?? null
  };
}
function walletDefiToPositions(defi, chainId) {
  const out = [];
  for (const proto of defi.protocols ?? []) {
    const protocolId = (proto.protocol_id ?? proto.protocol_name ?? "unknown").toLowerCase();
    (proto.positions ?? []).forEach((pos, i) => {
      out.push({
        id: `${pos.address ?? protocolId}-${i}`,
        protocolId,
        chainId,
        kind: defiKindFromLabel(pos.label),
        label: pos.label ?? proto.protocol_name ?? void 0,
        supplied: (pos.tokens ?? []).map((t) => tokenToAsset(t, chainId)),
        tvlUsd: pos.balance_usd ?? null,
        netUsd: pos.balance_usd ?? null,
        source: "moralis",
        metadata: {
          protocol_name: proto.protocol_name ?? void 0,
          protocol_logo: proto.protocol_logo ?? void 0,
          protocol_url: proto.protocol_url ?? void 0
        }
      });
    });
  }
  return out;
}
function walletNftItemToDto(item, chainId) {
  const meta = item.metadata_parsed ?? {};
  const collection = (item.token_address ?? "").toLowerCase();
  const metaName = typeof meta.name === "string" ? meta.name : null;
  const metaImage = typeof meta.image === "string" ? meta.image : null;
  const metaDesc = typeof meta.description === "string" ? meta.description : null;
  return {
    org_id: "main",
    tenant_id: "main",
    org_id_tenant_id: "main-main",
    customer_id: null,
    customer_org_tenant_pk_v2: "",
    chain_id: chainId,
    family: "evm",
    collection,
    token_id: item.token_id ?? "",
    owner: item.owner_of ?? null,
    name: item.name ?? metaName,
    description: metaDesc,
    image_uri: metaImage,
    metadata_uri: item.token_uri ?? null,
    collection_info: null,
    supply: item.amount ?? null,
    source: "moralis",
    metadata: {
      ...meta,
      rarity_rank: item.rarity_rank,
      rarity_percentage: item.rarity_percentage,
      rarity_label: item.rarity_label,
      floor_price_usd: item.floor_price_usd,
      possible_spam: item.possible_spam,
      verified_collection: item.verified_collection,
      list_price: item.list_price
    },
    chain_id_collection: `${chainId}#${collection}`
  };
}
function walletNftsToDtos(page, chainId) {
  return (page.items ?? []).map((item) => walletNftItemToDto(item, chainId));
}
function nftCollectionViewToCollection(view) {
  return {
    address: view.contract_address,
    name: view.name ?? null,
    symbol: view.symbol ?? null,
    isVerified: view.verified_collection ?? false,
    logoUrl: view.collection_logo ?? null,
    floorPriceUsd: view.floor_price_usd ?? null
  };
}

// src/addresses/helpers.ts
var PROTECTED_FIELDS = /* @__PURE__ */ new Set([
  "chain",
  "address",
  "customer_id",
  "org_id",
  "tenant_id",
  "org_id_tenant_id",
  "customer_org_tenant_pk_v2",
  "id",
  "created_at",
  "created_by",
  "created_by_service",
  "updated_at",
  "updated_by",
  "updated_by_service"
]);
function addressRefPath(chain, address) {
  return `addresses/${encodeURIComponent(chain)}/${encodeURIComponent(address)}`;
}
function clampAddressLimit(limit) {
  if (limit == null || Number.isNaN(limit)) return ADDRESS_DEFAULT_PAGE_LIMIT;
  return Math.max(1, Math.min(Math.trunc(limit), ADDRESS_MAX_PAGE_LIMIT));
}
function addressEditableBody(fields) {
  const out = {};
  for (const [k, v] of Object.entries(fields)) {
    if (PROTECTED_FIELDS.has(k)) continue;
    if (v === void 0) continue;
    out[k] = v;
  }
  return out;
}
function existingPrimaryChains(rows, address) {
  const target = (address ?? "").trim().toLowerCase();
  if (!target) return [];
  const seen = /* @__PURE__ */ new Set();
  for (const r of rows) {
    if (!r?.is_primary_chain) continue;
    if (String(r.address ?? "").trim().toLowerCase() !== target) continue;
    const chain = String(r.chain ?? "").trim();
    if (chain) seen.add(chain);
  }
  return [...seen];
}
function summarizeActivity(activity) {
  if (!activity) {
    return {
      activeChains: 0,
      totalTx: 0,
      totalDefi: 0,
      totalNft: 0,
      totalTokenTransfers: 0
    };
  }
  const chainCount = activity.active_chain_ids?.length || Object.keys(activity.chains ?? {}).length;
  return {
    activeChains: chainCount,
    totalTx: activity.total_tx_count ?? 0,
    totalDefi: activity.total_defi_count ?? 0,
    totalNft: activity.total_nft_count ?? 0,
    totalTokenTransfers: activity.total_token_transfer_count ?? 0,
    firstSeenAt: activity.first_seen_at ?? null,
    lastSeenAt: activity.last_seen_at ?? null
  };
}

// src/api/unwrap.ts
var ApiError = class extends Error {
  constructor(envelope) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.name = "ApiError";
    this.statusCode = envelope.statusCode;
    this.code = envelope.error.error_code;
    this.level = envelope.error.error_level;
    this.details = envelope.error.details;
    this.correlationId = envelope.error.correlation_id;
  }
  envelope;
  statusCode;
  code;
  level;
  details;
  correlationId;
};
function unwrapResponse(res) {
  if (res.ok) return res;
  throw new ApiError(res);
}
function unwrapData(res) {
  return unwrapResponse(res).data;
}
var ScopeOptionSchema = z.object({
  scope: z.string(),
  readable: z.boolean().default(true),
  count: z.number().int().default(1),
  overrides: z.record(z.string(), z.string()).default({})
});
var CrossChainMemberSchema = z.object({
  tx_id: z.union([z.string(), z.null()]).default(null),
  tx_hash: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  role: z.union([z.string(), z.null()]).default(null),
  is_self: z.boolean().default(false),
  // Per-leg execution outcome, so the card can flag a reverted counterpart
  // leg (a bridge destination that landed but reverted) with its reason.
  // `status` is the semantic status (`success`/`failed`/…), `receipt_status`
  // the raw EVM word (1/0), `error` the revert reason; `failed` is derived.
  status: z.union([z.string(), z.null()]).default(null),
  receipt_status: z.union([z.number(), z.null()]).default(null),
  error: z.union([z.string(), z.null()]).default(null),
  failed: z.boolean().default(false)
}).catchall(z.any());
var CrossChainGroupSchema = z.object({
  group_id: z.string(),
  saga_kind: z.union([z.string(), z.null()]).default(null),
  relationship_type: z.union([z.string(), z.null()]).default(null),
  linkage_model: z.union([z.string(), z.null()]).default(null),
  group_scope: z.union([z.string(), z.null()]).default(null),
  role: z.union([z.string(), z.null()]).default(null),
  confidence: z.union([z.number(), z.null()]).default(null),
  confidence_tier: z.union([z.string(), z.null()]).default(null),
  state: z.union([z.string(), z.null()]).default(null),
  member_count: z.union([z.number(), z.null()]).default(null),
  members: z.array(CrossChainMemberSchema).default([]),
  // Live grouping lifecycle: canonical roles still expected (a lone source's
  // `["destination"]`), whether the group is waiting on a counterpart, and the
  // unix-seconds timeout — so the detail card can show "awaiting destination"
  // with a countdown, symmetric with the group page.
  expected_roles: z.array(z.string()).default([]),
  missing_roles: z.array(z.string()).default([]),
  awaiting: z.boolean().default(false),
  // True for an open-ended position group (a wallet's `trade.token_position`:
  // its buys + sells of one token) — it accretes peer legs (`buy`/`sell`) and
  // has no fixed counterpart, so the card renders it as a trading position
  // rather than a source↔destination bridge saga (never "awaiting a leg").
  open_ended: z.boolean().default(false),
  // For an open-ended position: the token address the position is in + the
  // venue it traded on (e.g. `uniswap v2`), lifted off the legs so the card
  // can title the position by token. `null` for a cross-chain bridge saga.
  token: z.union([z.string(), z.null()]).default(null),
  venue: z.union([z.string(), z.null()]).default(null),
  expires_at: z.union([z.number(), z.null()]).default(null),
  // True when any leg's execution reverted (a broken saga) — derived from the
  // members' per-leg status so the card can surface it even while state=open.
  failed: z.boolean().default(false)
}).catchall(z.any());
var ChainForkSchema = z.object({
  chain_id: z.string(),
  canonical_tx_id: z.union([z.string(), z.null()]).default(null),
  tx_hash: z.union([z.string(), z.null()]).default(null)
}).catchall(z.any());
z.object({
  canonical_tx_id: z.string(),
  chain_id: z.union([z.string(), z.null()]).default(null),
  tx_hash: z.union([z.string(), z.null()]).default(null),
  // Tenancy of the SERVED row — lets the FE show whose/which copy this is.
  customer_id: z.union([z.string(), z.null()]).default(null),
  org_id: z.union([z.string(), z.null()]).default(null),
  tenant_id: z.union([z.string(), z.null()]).default(null),
  user_id: z.union([z.string(), z.null()]).default(null),
  // Body pointer (the heavy payload stays in S3 / on the CDN).
  has_body: z.boolean().default(false),
  body_key: z.union([z.string(), z.null()]).default(null),
  body_etag: z.union([z.string(), z.null()]).default(null),
  body: z.union([z.record(z.string(), z.any()), z.null()]).default(null),
  // Manifest of heavy body parts offloaded to sibling S3 objects (`raw` /
  // `events` / `decoded`), each with its own byte size + etag. Empty when the
  // whole body is a single "core" object. Lets the detail UI paint the core
  // immediately and lazy-load + merge the heavy slices on demand (mirrors
  // `TransactionRow.body_slices` / `EnvelopeHeader.body_slices`).
  body_slices: z.record(
    z.string(),
    z.object({
      bytes: z.number().int().default(0),
      etag: z.union([z.string(), z.null()]).default(null)
    }).catchall(z.any())
  ).default({}),
  // Async background-enrichment state. `processing` = the decode/graph is
  // running on the pipeline (the row is a fast stub with no body yet), `ready`
  // = the body/graph have landed (== `has_body`), `failed` = the run errored,
  // `null` = the tx was never part of the async flow (already enriched / sync
  // live-fill). `run_id` is the enqueued pipeline run, for polling/diagnostics.
  // The FE polls the detail while `processing`, then loads the body on `ready`.
  enrichment_status: z.union([z.string(), z.null()]).default(null),
  run_id: z.union([z.string(), z.null()]).default(null),
  // Workspace + cross-scope affordance.
  saved: z.boolean().default(false),
  other_scopes: z.array(ScopeOptionSchema).default([]),
  // Cross-chain saga groups this tx is a leg of (source↔destination linkage),
  // resolved at read time from the protocols' `cross_chain_refs`.
  cross_chain_groups: z.array(CrossChainGroupSchema).default([]),
  // Forked chains (ETH/ETC) the SAME raw hash is also on — the same hash is a
  // distinct tx per chain, so each is a separate row the FE can switch to.
  chain_forks: z.array(ChainForkSchema).default([])
}).catchall(z.any());
function scopeReadQuery(scope, opts = {}) {
  const query = {};
  if (opts.includeBody) query.include_body = true;
  if (opts.enqueue === false) query.enqueue = false;
  if (scope) {
    for (const [key, value] of Object.entries(scope.overrides ?? {})) {
      query[key] = value;
    }
  }
  return query;
}

// src/addresses/api.ts
function createAddressesApi(client, options = {}) {
  const base = (options.basePath ?? "/addresses").replace(/\/+$/, "");
  const ref = (chain, address) => `${base}/${encodeURIComponent(chain)}/${encodeURIComponent(address)}`;
  return {
    async list(query, signal) {
      const q = {
        scope: query?.scope ?? "tenant",
        limit: clampAddressLimit(query?.limit)
      };
      if (query?.entity_id) q.entity_id = query.entity_id;
      if (query?.include_global) q.include_global = true;
      if (query?.cursor) q.cursor = query.cursor;
      const page = unwrapData(await client.get(base, { query: q, signal, cacheTtlMs: 0 }));
      const addresses = Array.isArray(page?.addresses) ? page.addresses : [];
      return { addresses, next_cursor: page?.next_cursor ?? null, count: page?.count ?? addresses.length };
    },
    async get(chain, address, signal) {
      return unwrapData(await client.get(ref(chain, address), { signal, cacheTtlMs: 0 })).address;
    },
    async getDetail(chain, address, opts, signal) {
      const query = { ...scopeReadQuery(opts?.scope ?? null) };
      if (opts?.withScopes) query.scopes = true;
      const res = unwrapData(
        await client.get(ref(chain, address), { query, signal, cacheTtlMs: 0 })
      );
      return {
        address: res?.address,
        saved: res?.saved ?? false,
        catalog: res?.catalog,
        other_scopes: Array.isArray(res?.other_scopes) ? res.other_scopes : []
      };
    },
    async getByAddress(address, signal) {
      const res = unwrapData(
        await client.get(base, {
          query: { scope: "cross_chain", address },
          signal,
          cacheTtlMs: 0
        })
      );
      const addresses = Array.isArray(res?.addresses) ? res.addresses : [];
      return { addresses, activity: res?.activity ?? null, count: res?.count ?? addresses.length };
    },
    async create(input) {
      const { chain, address, ...rest } = input;
      const body = { chain, address, ...addressEditableBody(rest) };
      return unwrapData(await client.post(base, body)).address;
    },
    async createMany(input) {
      const body = {
        ...addressEditableBody(input.fields ?? {}),
        address: input.address
      };
      if (input.chains?.length) body.chains = input.chains;
      if (input.all_chains) body.all_chains = true;
      if (input.per_chain && Object.keys(input.per_chain).length > 0) {
        body.per_chain = Object.fromEntries(
          Object.entries(input.per_chain).map(([chain, fields]) => [
            chain,
            addressEditableBody(fields ?? {})
          ])
        );
      }
      if (input.assignments?.length) {
        body.assignments = input.assignments.map((a) => ({
          catalog_kind: a.catalog_kind,
          catalog_id: a.catalog_id,
          user_scoped: a.user_scoped ?? true,
          ...a.visible_to_user_ids ? { visible_to_user_ids: a.visible_to_user_ids } : {},
          ...a.visible_to_team_ids ? { visible_to_team_ids: a.visible_to_team_ids } : {}
        }));
      }
      if (input.set_primary_entity_id) body.set_primary_entity_id = input.set_primary_entity_id;
      const res = unwrapData(await client.post(base, body));
      return {
        results: Array.isArray(res?.results) ? res.results : [],
        addresses: Array.isArray(res?.addresses) ? res.addresses : [],
        count: res?.count ?? 0,
        failed: res?.failed ?? 0,
        overlays: Array.isArray(res?.overlays) ? res.overlays : [],
        primary: Array.isArray(res?.primary) ? res.primary : []
      };
    },
    async update(chain, address, changes) {
      return unwrapData(
        await client.patch(ref(chain, address), addressEditableBody(changes))
      ).address;
    },
    async remove(chain, address) {
      unwrapData(await client.delete(ref(chain, address)));
    },
    async getActivity(chain, address, signal) {
      const res = unwrapData(
        await client.get(`${ref(chain, address)}/activity`, {
          signal,
          cacheTtlMs: 0
        })
      );
      return res.activity ?? null;
    },
    async listAssignments(chain, address, signal) {
      const res = unwrapData(
        await client.get(`${ref(chain, address)}/assignments`, {
          signal,
          cacheTtlMs: 0
        })
      );
      return res.assignments ?? [];
    },
    async attachAssignment(chain, address, input) {
      return unwrapData(
        await client.post(`${ref(chain, address)}/assignments`, {
          catalog_kind: input.catalog_kind,
          catalog_id: input.catalog_id,
          user_scoped: input.user_scoped ?? true,
          visible_to_user_ids: input.visible_to_user_ids,
          visible_to_team_ids: input.visible_to_team_ids
        })
      ).assignment;
    },
    async detachAssignment(chain, address, input) {
      unwrapData(
        await client.delete(`${ref(chain, address)}/assignments`, {
          query: {
            catalog_kind: input.catalog_kind,
            catalog_id: input.catalog_id,
            user_scoped: input.user_scoped ?? true
          }
        })
      );
    },
    async share(chain, address, input) {
      return unwrapData(
        await client.post(`${ref(chain, address)}/share`, {
          catalog_kind: input.catalog_kind,
          catalog_id: input.catalog_id,
          user_scoped: input.user_scoped ?? true,
          add_user_ids: input.add_user_ids,
          add_team_ids: input.add_team_ids,
          remove_user_ids: input.remove_user_ids,
          remove_team_ids: input.remove_team_ids
        })
      ).assignment;
    },
    async setPrimary(chain, address, entityId) {
      return unwrapData(
        await client.put(`${ref(chain, address)}/primary`, {
          entity_id: entityId
        })
      ).primary;
    },
    async clearPrimary(chain, address, entityId) {
      unwrapData(
        await client.delete(`${ref(chain, address)}/primary`, { query: { entity_id: entityId } })
      );
    },
    async getEntityMapping(chain, address, signal) {
      const res = await client.get(`${ref(chain, address)}/entity`, {
        signal,
        cacheTtlMs: 0
      });
      if (res.ok) {
        return res.data?.mapping ?? res.result?.mapping ?? null;
      }
      if (res.statusCode === 404) return null;
      unwrapData(res);
      return null;
    },
    async putEntityMapping(chain, address, input) {
      return unwrapData(
        await client.put(`${ref(chain, address)}/entity`, {
          entity_id: input.entity_id,
          ...input.role !== void 0 ? { role: input.role } : {},
          ...input.confidence !== void 0 ? { confidence: input.confidence } : {},
          ...input.valid_from !== void 0 ? { valid_from: input.valid_from } : {},
          ...input.valid_to !== void 0 ? { valid_to: input.valid_to } : {},
          ...input.source !== void 0 ? { source: input.source } : {},
          ...input.metadata !== void 0 ? { metadata: input.metadata } : {}
        })
      ).mapping;
    },
    async deleteEntityMapping(chain, address) {
      unwrapData(await client.delete(`${ref(chain, address)}/entity`));
    },
    async enrich(chain, address, signal) {
      const res = unwrapData(
        await client.get(`${ref(chain, address)}/3rd-parties`, {
          signal,
          cacheTtlMs: 0
        })
      );
      return res.activity ?? null;
    },
    async listWalletTransactions(chain, address, query, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/transactions`, {
          query: {
            ...query?.limit ? { limit: query.limit } : {},
            ...query?.cursor ? { cursor: query.cursor } : {}
          },
          signal,
          cacheTtlMs: 0
        })
      );
    },
    async getDefi(chain, address, query, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/defi`, {
          query: {
            ...query?.chain ? { chain: query.chain } : {},
            ...query?.protocol ? { protocol: query.protocol } : {}
          },
          signal,
          cacheTtlMs: 0
        })
      );
    },
    async getNfts(chain, address, query, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/nfts`, {
          query: {
            ...query?.limit ? { limit: query.limit } : {},
            ...query?.cursor ? { cursor: query.cursor } : {}
          },
          signal,
          cacheTtlMs: 0
        })
      );
    },
    async getNftCollection(chain, address, query, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/nft-collection`, {
          query: {
            ...query?.limit ? { limit: query.limit } : {},
            ...query?.cursor ? { cursor: query.cursor } : {}
          },
          signal,
          cacheTtlMs: 0
        })
      );
    },
    async getDomain(chain, address, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/domain`, {
          signal,
          cacheTtlMs: 0
        })
      );
    }
  };
}

export { ADDRESS_DEFAULT_PAGE_LIMIT, ADDRESS_LIST_SCOPES, ADDRESS_MAX_PAGE_LIMIT, ADDRESS_ROW_SCOPES, ADDRESS_ROW_SCOPE_DESCRIPTIONS, ADDRESS_ROW_SCOPE_LABELS, ADDRESS_SCOPES, ADDRESS_TYPES, ADDRESS_TYPE_LABEL, addressEditableBody, addressRefPath, addressRowScopeDescription, addressRowScopeLabel, clampAddressLimit, createAddressesApi, defiKindFromLabel, existingPrimaryChains, isContractType, nftCollectionViewToCollection, summarizeActivity, walletDataPaths, walletDefiToPositions, walletNftItemToDto, walletNftsToDtos };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map